import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Slider,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import { Schedule, CreateScheduleRequest } from '../../models/Schedule';
import { MusicFolderNode } from '../../models/Zone';
import * as zoneService from '../../services/newZoneService';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeDownRoundedIcon from '@mui/icons-material/VolumeDownRounded';
import ShuffleRoundedIcon from '@mui/icons-material/ShuffleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FolderTree from './FolderTree';

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (scheduleData: CreateScheduleRequest) => void;
  zoneId?: number;
  zoneName?: string;
  schedule?: Schedule | null;
  mode: 'create' | 'edit';
}

const dayLabels: Record<string, string> = {
  '1': 'Paz', '2': 'Pzt', '3': 'Sal', '4': 'Çar', '5': 'Per', '6': 'Cum', '7': 'Cmt',
};

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
  open, onClose, onSave, zoneId, zoneName, schedule, mode,
}) => {
  const [musicFolders, setMusicFolders] = useState<string[]>([]);
  const [musicFoldersTree, setMusicFoldersTree] = useState<MusicFolderNode[]>([]);
  const [musicSubfolders, setMusicSubfolders] = useState<{ [key: string]: string[] }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateScheduleRequest>({
    name: '',
    description: '',
    zoneId: zoneId || 0,
    daysOfWeek: '1,2,3,4,5,6,7',
    startTime: '08:00',
    endTime: '23:00',
    volume: 1,
    musicFolders: [],
    shuffle: true,
    priority: 0,
    isActive: true,
  });

  const [days, setDays] = useState<Record<string, boolean>>({
    '1': true, '2': true, '3': true, '4': true, '5': true, '6': true, '7': true,
  });

  const volumeDisplay = `${Math.round(formData.volume * 100)}%`;

  const modernInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px', backgroundColor: '#fafafa',
      '& fieldset': { borderColor: '#e8e8e8' },
      '&:hover fieldset': { borderColor: 'rgba(66, 148, 227, 0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#4294e3', borderWidth: '2px' },
    },
    '& .MuiInputLabel-root': { color: '#888', '&.Mui-focused': { color: '#4294e3' } },
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [folders, foldersTree] = await Promise.all([
          zoneService.getMusicFolders(true),
          zoneService.getMusicFoldersTree(true),
        ]);
        setMusicFolders(folders);
        setMusicFoldersTree(foldersTree);

        const subfoldersPromises = folders.map(async (folder) => {
          try {
            const subfolders = await zoneService.getMusicSubfolders(folder, true);
            return { folder, subfolders };
          } catch (err) {
            return { folder, subfolders: [] };
          }
        });
        const subfoldersResults = await Promise.all(subfoldersPromises);
        const subfoldersMap: { [key: string]: string[] } = {};
        subfoldersResults.forEach((result) => { subfoldersMap[result.folder] = result.subfolders; });
        setMusicSubfolders(subfoldersMap);

        if (mode === 'edit' && schedule) {
          setFormData({
            name: schedule.name,
            description: schedule.description || '',
            zoneId: schedule.zoneId,
            daysOfWeek: schedule.daysOfWeek,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            volume: schedule.volume,
            musicFolders: schedule.musicFolders,
            shuffle: schedule.shuffle,
            priority: schedule.priority,
            isActive: schedule.isActive,
          });

          const daysOfWeekArray = schedule.daysOfWeek.split(',');
          const initialDays: Record<string, boolean> = { '1': false, '2': false, '3': false, '4': false, '5': false, '6': false, '7': false };
          daysOfWeekArray.forEach((day) => { if (day.trim()) initialDays[day.trim()] = true; });
          setDays(initialDays);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Müzik klasörleri yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [mode, schedule]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateDays = (day: string, checked: boolean) => {
    setDays((prev) => ({ ...prev, [day]: checked }));
    const updatedDays = { ...days, [day]: checked };
    const dayString = Object.entries(updatedDays).filter(([_, v]) => v).map(([k]) => k).join(',');
    setFormData((prev) => ({ ...prev, daysOfWeek: dayString }));
  };

  const handleVolumeChange = (_: any, newValue: number | number[]) => {
    setFormData((prev) => ({ ...prev, volume: newValue as number }));
  };

  const selectAllDays = () => {
    setDays({ '1': true, '2': true, '3': true, '4': true, '5': true, '6': true, '7': true });
    setFormData((prev) => ({ ...prev, daysOfWeek: '1,2,3,4,5,6,7' }));
  };
  const selectWeekdays = () => {
    setDays({ '1': false, '2': true, '3': true, '4': true, '5': true, '6': true, '7': false });
    setFormData((prev) => ({ ...prev, daysOfWeek: '2,3,4,5,6' }));
  };
  const selectWeekends = () => {
    setDays({ '1': true, '2': false, '3': false, '4': false, '5': false, '6': false, '7': true });
    setFormData((prev) => ({ ...prev, daysOfWeek: '1,7' }));
  };

  const handleFolderSelectionChange = (selectedPaths: string[]) => {
    setFormData((prev) => ({ ...prev, musicFolders: selectedPaths }));
  };

  const handleSubmit = () => {
    if (!formData.name) { setError('Zamanlama adı gerekli'); return; }
    if (!formData.daysOfWeek) { setError('En az bir gün seçilmeli'); return; }
    if (!formData.startTime || !formData.endTime) { setError('Başlangıç ve bitiş saati gerekli'); return; }
    if (formData.musicFolders.length === 0) { setError('En az bir müzik klasörü seçilmeli'); return; }
    console.log('ScheduleDialog sending formData:', formData);
    onSave(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          maxHeight: '85vh'
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0', py: 2, px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            backgroundColor: 'rgba(66, 148, 227, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <CalendarTodayRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', lineHeight: 1.3 }}>
              {mode === 'create' ? 'Zamanlama Ekle' : 'Zamanlama Düzenle'}
            </Typography>
            {zoneName && (
              <Typography sx={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.3 }}>
                {zoneName}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ width: 32, height: 32, color: '#999', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}>
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{
        px: 3, pb: 3, pt: '24px !important', overflow: 'auto',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '3px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.15) transparent',
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', backgroundColor: 'rgba(229, 57, 53, 0.05)', border: '1px solid rgba(229, 57, 53, 0.15)' }}>
            {error}
          </Alert>
        )}

        {/* Schedule Name + Priority */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
          <TextField
            name="name"
            label="Zamanlama Adı"
            fullWidth required
            value={formData.name}
            onChange={handleInputChange}
            sx={modernInputSx}
          />
          <TextField
            name="priority"
            label="Öncelik"
            type="number"
            value={formData.priority}
            onChange={handleInputChange}
            InputProps={{
              inputProps: { min: 0 },
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="Düşük sayı = yüksek öncelik (0 en yüksek)">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#bbb' }} />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ ...modernInputSx, minWidth: 120, maxWidth: 140 }}
          />
        </Box>

        {/* Days Selection */}
        <Box sx={{ mb: 2.5, p: 2, borderRadius: '14px', border: '1px solid #f0f0f0', backgroundColor: '#fafcff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayRoundedIcon sx={{ fontSize: 15, color: '#4294e3' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e' }}>Günler</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[
                { label: 'Hepsi', fn: selectAllDays },
                { label: 'Hafta İçi', fn: selectWeekdays },
                { label: 'Hafta Sonu', fn: selectWeekends },
              ].map((preset) => (
                <Button key={preset.label} size="small" onClick={preset.fn} sx={{
                  textTransform: 'none', fontSize: '0.65rem', fontWeight: 500, borderRadius: '8px',
                  color: '#4294e3', minWidth: 0, px: 1, py: 0.3,
                  '&:hover': { backgroundColor: 'rgba(66, 148, 227, 0.08)' }
                }}>
                  {preset.label}
                </Button>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {Object.entries(dayLabels).map(([key, label]) => (
              <Box
                key={key}
                onClick={() => updateDays(key, !days[key])}
                sx={{
                  width: 48, height: 40, borderRadius: '10px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 600,
                  border: days[key] ? '2px solid #4294e3' : '1px solid #e0e0e0',
                  backgroundColor: days[key] ? 'rgba(66, 148, 227, 0.08)' : '#fff',
                  color: days[key] ? '#4294e3' : '#888',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: days[key] ? '#4294e3' : '#bbb' }
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Time Range */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 14, color: '#aaa' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500 }}>Başlangıç</Typography>
            </Box>
            <TextField
              name="startTime" type="time" fullWidth required
              value={formData.startTime} onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              sx={modernInputSx}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 14, color: '#aaa' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500 }}>Bitiş</Typography>
            </Box>
            <TextField
              name="endTime" type="time" fullWidth required
              value={formData.endTime} onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              sx={modernInputSx}
            />
          </Box>
        </Box>

        {/* Volume + Shuffle */}
        <Box sx={{ mb: 2.5, p: 2, borderRadius: '14px', border: '1px solid #f0f0f0', backgroundColor: '#fafcff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <VolumeUpRoundedIcon sx={{ fontSize: 15, color: '#4294e3' }} />
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e' }}>Ses Seviyesi</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#4294e3', fontWeight: 600, ml: 'auto' }}>{volumeDisplay}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, gap: 1 }}>
            <VolumeDownRoundedIcon sx={{ fontSize: 16, color: '#bbb' }} />
            <Slider
              value={formData.volume}
              onChange={handleVolumeChange}
              min={0} max={1} step={0.01}
              valueLabelDisplay="auto"
              valueLabelFormat={volumeDisplay}
              sx={{
                color: '#4294e3',
                '& .MuiSlider-thumb': { width: 16, height: 16 },
                '& .MuiSlider-rail': { backgroundColor: '#e0e0e0' }
              }}
            />
            <VolumeUpRoundedIcon sx={{ fontSize: 16, color: '#bbb' }} />
          </Box>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
            <Box
              onClick={() => setFormData((prev) => ({ ...prev, shuffle: !prev.shuffle }))}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                px: 1.5, py: 0.8, borderRadius: '10px',
                border: formData.shuffle ? '2px solid #4294e3' : '1px solid #e0e0e0',
                backgroundColor: formData.shuffle ? 'rgba(66, 148, 227, 0.06)' : '#fff',
                transition: 'all 0.15s'
              }}
            >
              <Checkbox
                checked={formData.shuffle}
                size="small"
                sx={{ p: 0, color: '#ccc', '&.Mui-checked': { color: '#4294e3' } }}
              />
              <ShuffleRoundedIcon sx={{ fontSize: 16, color: formData.shuffle ? '#4294e3' : '#bbb' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: formData.shuffle ? 600 : 400, color: formData.shuffle ? '#4294e3' : '#888' }}>
                Karışık Çalma
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Music Folders */}
        <Box sx={{ p: 2, borderRadius: '14px', border: '1px solid #f0f0f0', backgroundColor: '#fafcff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FolderOpenRoundedIcon sx={{ fontSize: 15, color: '#4294e3' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e' }}>Müzik Klasörleri</Typography>
              <Tooltip title="Bu zamanlama sırasında çalınacak klasörleri seçin">
                <InfoOutlinedIcon sx={{ fontSize: 14, color: '#bbb', cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                onClick={() => {
                  const getAllPaths = (nodes: MusicFolderNode[]): string[] => {
                    let paths: string[] = [];
                    nodes.forEach((node) => {
                      paths.push(node.path);
                      if (node.children && node.children.length > 0) paths = [...paths, ...getAllPaths(node.children)];
                    });
                    return paths;
                  };
                  let allPaths: string[] = [];
                  if (musicFoldersTree.length > 0) { allPaths = getAllPaths(musicFoldersTree); }
                  else { musicFolders.forEach((f) => { allPaths.push(f); (musicSubfolders[f] || []).forEach((s) => allPaths.push(`${f}/${s}`)); }); }
                  handleFolderSelectionChange(allPaths);
                }}
                sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 500, color: '#4294e3', borderRadius: '8px', minWidth: 0, px: 1.5, py: 0.3, '&:hover': { backgroundColor: 'rgba(66, 148, 227, 0.08)' } }}
              >
                Tümünü Seç
              </Button>
              <Button
                size="small"
                onClick={() => handleFolderSelectionChange([])}
                sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 500, color: '#999', borderRadius: '8px', minWidth: 0, px: 1.5, py: 0.3, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
              >
                Temizle
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Typography sx={{ fontSize: '0.8rem', color: '#999', textAlign: 'center', py: 3 }}>
              Klasörler yükleniyor...
            </Typography>
          ) : (
            <Box sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', overflow: 'hidden', backgroundColor: '#fff' }}>
              <FolderTree
                nodes={musicFoldersTree}
                selectedPaths={formData.musicFolders}
                onSelectionChange={handleFolderSelectionChange}
              />
            </Box>
          )}

          {/* Selected folders summary */}
          {formData.musicFolders.length > 0 && (
            <Box sx={{
              mt: 1.5, p: 1.5,
              display: 'flex', flexWrap: 'wrap', gap: 0.5,
              backgroundColor: 'rgba(66, 148, 227, 0.04)',
              borderRadius: '10px', border: '1px solid rgba(66, 148, 227, 0.12)',
            }}>
              <Typography sx={{
                width: '100%', mb: 0.5,
                fontSize: '0.75rem', fontWeight: 600, color: '#4294e3',
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                Seçili klasörler ({formData.musicFolders.length}):
              </Typography>
              {formData.musicFolders.map((folder) => (
                <Chip
                  key={folder}
                  label={folder}
                  size="small"
                  sx={{
                    fontWeight: 500, fontSize: '0.75rem', height: 28, borderRadius: '8px',
                    backgroundColor: 'rgba(66, 148, 227, 0.08)', color: '#4294e3',
                    border: '1px solid rgba(66, 148, 227, 0.15)',
                    '& .MuiChip-deleteIcon': { color: 'rgba(66, 148, 227, 0.5)', fontSize: 16 },
                    '&:hover': { backgroundColor: 'rgba(66, 148, 227, 0.12)' },
                  }}
                  onDelete={() => handleFolderSelectionChange(formData.musicFolders.filter((f) => f !== folder))}
                  deleteIcon={<CloseRoundedIcon />}
                />
              ))}
            </Box>
          )}

          {formData.musicFolders.length === 0 && !loading && (
            <Typography sx={{ fontSize: '0.75rem', color: '#c62828', mt: 1 }}>
              En az bir müzik klasörü seçilmeli
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #f0f0f0', gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none', fontWeight: 500, borderRadius: '10px',
            color: '#666', backgroundColor: '#f5f5f5', px: 3, py: 0.8,
            '&:hover': { backgroundColor: '#eee' }
          }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSubmit}
          sx={{
            textTransform: 'none', fontWeight: 600, borderRadius: '10px',
            backgroundColor: '#4294e3', color: '#fff', px: 3, py: 0.8,
            '&:hover': { backgroundColor: '#3680c9' }
          }}
        >
          {mode === 'create' ? 'Oluştur' : 'Güncelle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;
